import { ChangeEvent, Fragment, ReactElement } from 'react';

import { Cell, CellText, FormLabel, Radio, VSpacingContainer } from '@hh.ru/magritte-ui';

import useTrls from 'hooks/translation/useTrls';

import styles from 'components/Admin/AdfoxInfo/RadioGroup/RadioGroup.module.less';

interface RadioGroupProps<T extends string> {
    title: string;
    name: string;
    options: T[];
    value: T;
    labels: Record<T, string>;
    dataQaPrefix: string;
    renderPreview?: (option: T) => ReactElement;
    renderHint?: (option: T) => ReactElement | null;
    onChange: (value: T) => void;
}

export const RadioGroup = <T extends string>({
    title,
    name,
    options,
    value,
    labels,
    dataQaPrefix,
    renderPreview,
    renderHint,
    onChange,
}: RadioGroupProps<T>): ReactElement => {
    const trls = useTrls();

    const renderOption = (option: T) => (
        <Cell
            left={
                <Radio
                    name={name}
                    value={option}
                    checked={value === option}
                    onChange={(event: ChangeEvent<HTMLInputElement>) => onChange(event.target.value as T)}
                    data-qa={`${dataQaPrefix}-${option}`}
                />
            }
        >
            <div className={styles.optionLabel}>
                <CellText>{trls(labels[option])}</CellText>
                {renderHint?.(option)}
            </div>
        </Cell>
    );

    return (
        <VSpacingContainer default={12}>
            <FormLabel>{title}</FormLabel>
            {renderPreview ? (
                <div className={styles.optionsGrid}>
                    {options.map((option) => (
                        <Fragment key={option}>
                            {renderOption(option)}
                            {renderPreview(option)}
                        </Fragment>
                    ))}
                </div>
            ) : (
                options.map((option) => <Fragment key={option}>{renderOption(option)}</Fragment>)
            )}
        </VSpacingContainer>
    );
};
